import React, { useState } from 'react';
import {
  BankMarketingInput,
  BankMarketingResponse,
  BankMarketingModelResult,
  Job,
  Marital,
  Education,
  BinaryUnknown,
  Contact,
  Month,
  DayOfWeek,
  Poutcome,
  ModelType,
} from '../types/bankMarketing';
import { api } from '../services/api';
import { getEnumLabel } from '../utils/formatters';
import './BankMarketingForm.css';

const BankMarketingForm: React.FC = () => {
  // Initial form state
  const initialFormState: BankMarketingInput = {
    age: 40,
    job: Job.MANAGEMENT,
    marital: Marital.MARRIED,
    education: Education.UNIVERSITY_DEGREE,
    default: BinaryUnknown.NO,
    housing: BinaryUnknown.YES,
    loan: BinaryUnknown.NO,
    contact: Contact.CELLULAR,
    month: Month.MAY,
    day_of_week: DayOfWeek.MON,
    campaign: 1,
    pdays: 999,
    previous: 0,
    poutcome: Poutcome.NONEXISTENT,
    emp_var_rate: 1.1,
    cons_price_idx: 93.994,
    cons_conf_idx: -36.4,
    euribor3m: 4.857,
    nr_employed: 5191.0,
  };

  // Form state
  const [formData, setFormData] = useState<BankMarketingInput>(initialFormState);
  const [result, setResult] = useState<BankMarketingResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Handle numeric inputs
    if (
      name === 'age' ||
      name === 'campaign' ||
      name === 'pdays' ||
      name === 'previous' ||
      name === 'emp_var_rate' ||
      name === 'cons_price_idx' ||
      name === 'cons_conf_idx' ||
      name === 'euribor3m' ||
      name === 'nr_employed'
    ) {
      setFormData({
        ...formData,
        [name]: parseFloat(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.predictBankSubscription(formData);

      if (response.status === 'success' && response.data) {
        setResult(response.data);
      } else {
        setError(response.error || 'An error occurred while predicting subscription.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Create dropdown options from enum
  const renderEnumOptions = (enumObj: Record<string, string>) => {
    return Object.entries(enumObj).map(([key, value]) => (
      <option key={value} value={value}>
        {getEnumLabel(key)}
      </option>
    ));
  };

  // Format model type for display
  const formatModelType = (modelType: ModelType): string => {
    switch (modelType) {
      case ModelType.RANDOM_FOREST:
        return 'Random Forest';
      case ModelType.DECISION_TREE:
        return 'Decision Tree';
      case ModelType.KNN:
        return 'K-Nearest Neighbors';
      case ModelType.SVC:
        return 'Support Vector Classifier';
      case ModelType.LOGISTIC_REGRESSION:
        return 'Logistic Regression';
      default:
        return modelType;
    }
  };

  return (
    <div className="bank-marketing-form">
      <h2>Bank Marketing Prediction</h2>
      <p>
        Fill out the form below to predict whether a client will subscribe to a term deposit based
        on various factors.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Client Information</h3>
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="age">Age:</label>
                <input
                  type="number"
                  id="age"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  min={18}
                  max={100}
                  required
                />
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="job">Job:</label>
                <select id="job" name="job" value={formData.job} onChange={handleChange} required>
                  {renderEnumOptions(Job)}
                </select>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="marital">Marital Status:</label>
                <select
                  id="marital"
                  name="marital"
                  value={formData.marital}
                  onChange={handleChange}
                  required
                >
                  {renderEnumOptions(Marital)}
                </select>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="education">Education:</label>
                <select
                  id="education"
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                  required
                >
                  {renderEnumOptions(Education)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Financial Information</h3>
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="default">Has Credit in Default:</label>
                <select
                  id="default"
                  name="default"
                  value={formData.default}
                  onChange={handleChange}
                  required
                >
                  {renderEnumOptions(BinaryUnknown)}
                </select>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="housing">Has Housing Loan:</label>
                <select
                  id="housing"
                  name="housing"
                  value={formData.housing}
                  onChange={handleChange}
                  required
                >
                  {renderEnumOptions(BinaryUnknown)}
                </select>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="loan">Has Personal Loan:</label>
                <select
                  id="loan"
                  name="loan"
                  value={formData.loan}
                  onChange={handleChange}
                  required
                >
                  {renderEnumOptions(BinaryUnknown)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Contact Information</h3>
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="contact">Contact Type:</label>
                <select
                  id="contact"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  required
                >
                  {renderEnumOptions(Contact)}
                </select>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="month">Last Contact Month:</label>
                <select
                  id="month"
                  name="month"
                  value={formData.month}
                  onChange={handleChange}
                  required
                >
                  {renderEnumOptions(Month)}
                </select>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="day_of_week">Last Contact Day of Week:</label>
                <select
                  id="day_of_week"
                  name="day_of_week"
                  value={formData.day_of_week}
                  onChange={handleChange}
                  required
                >
                  {renderEnumOptions(DayOfWeek)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Campaign Information</h3>
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="campaign">Number of Contacts in Campaign:</label>
                <input
                  type="number"
                  id="campaign"
                  name="campaign"
                  value={formData.campaign}
                  onChange={handleChange}
                  min={1}
                  required
                />
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="pdays">Days Since Last Contact (999 if never contacted):</label>
                <input
                  type="number"
                  id="pdays"
                  name="pdays"
                  value={formData.pdays}
                  onChange={handleChange}
                  min={0}
                  required
                />
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="previous">Number of Previous Contacts:</label>
                <input
                  type="number"
                  id="previous"
                  name="previous"
                  value={formData.previous}
                  onChange={handleChange}
                  min={0}
                  required
                />
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="poutcome">Previous Outcome:</label>
                <select
                  id="poutcome"
                  name="poutcome"
                  value={formData.poutcome}
                  onChange={handleChange}
                  required
                >
                  {renderEnumOptions(Poutcome)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Economic Indicators</h3>
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="emp_var_rate">Employment Variation Rate:</label>
                <input
                  type="number"
                  id="emp_var_rate"
                  name="emp_var_rate"
                  value={formData.emp_var_rate}
                  onChange={handleChange}
                  step="0.1"
                  required
                />
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="cons_price_idx">Consumer Price Index:</label>
                <input
                  type="number"
                  id="cons_price_idx"
                  name="cons_price_idx"
                  value={formData.cons_price_idx}
                  onChange={handleChange}
                  step="0.001"
                  required
                />
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="cons_conf_idx">Consumer Confidence Index:</label>
                <input
                  type="number"
                  id="cons_conf_idx"
                  name="cons_conf_idx"
                  value={formData.cons_conf_idx}
                  onChange={handleChange}
                  step="0.1"
                  required
                />
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="euribor3m">Euribor 3 Month Rate:</label>
                <input
                  type="number"
                  id="euribor3m"
                  name="euribor3m"
                  value={formData.euribor3m}
                  onChange={handleChange}
                  step="0.001"
                  required
                />
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="nr_employed">Number of Employees:</label>
                <input
                  type="number"
                  id="nr_employed"
                  name="nr_employed"
                  value={formData.nr_employed}
                  onChange={handleChange}
                  step="0.1"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Predicting...' : 'Predict Subscription'}
        </button>
      </form>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {result && (
        <div className="result-container">
          <h3>Prediction Results</h3>

          <div className="model-predictions">
            {result.predictions.map((prediction: BankMarketingModelResult) => {
              const probabilityPercentage = prediction.subscription_probability
                ? Math.round(prediction.subscription_probability * 100)
                : null;

              return (
                <div key={prediction.ml_type} className="model-prediction-item">
                  <h4>{formatModelType(prediction.ml_type)}</h4>
                  <div className="prediction-result">
                    <strong>Prediction:</strong>&nbsp;
                    <span>
                      {prediction.will_subscribe ? 'Will Subscribe' : 'Will Not Subscribe'}
                    </span>
                  </div>

                  {probabilityPercentage !== null && (
                    <div className="prediction-probability">
                      <strong>Probability:</strong>&nbsp;{probabilityPercentage}%
                      <div className="probability-bar">
                        <div
                          className="probability-fill"
                          style={{ width: `${probabilityPercentage}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BankMarketingForm;
